import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Radio from "@mui/material/Radio";
import { debounce } from "../../../../utils";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./SelectBook.css";
import { setSelectBooks } from "../../../../redux-toolkit/features/theme/themeSlice";
import { useSelector, useDispatch } from "react-redux";

export const SelectBook = ({ setActivityOpen, activityOpen, allBooks }) => {
  const handleClose = () => setActivityOpen(false);
  const [books, setBooks] = useState([]);
  const [searchVal, setSearchVal] = useState("");
  const selectBooks = useSelector((state) => state.theme.selectBooks);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!activityOpen) {
      setSearchVal("");
      setBooks(allBooks || []);
      return;
    }
    setBooks(allBooks || []);
  }, [allBooks, activityOpen]);

  const filterBooks = useCallback(
    (value) => {
      const list = allBooks || [];
      if (!value?.trim()) {
        setBooks(list);
        return;
      }
      const query = value.toUpperCase();
      setBooks(list.filter((book) => book?.name?.toUpperCase().includes(query)));
    },
    [allBooks],
  );

  const setDebouncedSearchQuery = useMemo(
    () =>
      debounce((value) => {
        filterBooks(value);
      }, 300),
    [filterBooks],
  );

  const handleSearchChange = (value) => {
    setSearchVal(value);
    setDebouncedSearchQuery(value);
  };

  const handleSelect = (book) => {
    if (!book?._id) return;
    dispatch(setSelectBooks({ _id: book._id, name: book.name, type: book.type }));
    handleClose();
  };

  return (
    <Modal
      open={!!activityOpen}
      onClose={handleClose}
      aria-labelledby="select-book-title"
      aria-describedby="select-book-list"
    >
      <Box className="selectbook-modal">
        <div className="selectbook-modal__top">
          <h3 id="select-book-title" className="selectbook-modal__title">
            Select book
          </h3>
          <button
            type="button"
            className="selectbook-modal__close"
            onClick={handleClose}
            aria-label="Close"
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </button>
        </div>

        <div className="selectbook-modal__search">
          <SearchIcon sx={{ fontSize: 18, color: "var(--grey400)" }} />
          <input
            type="text"
            value={searchVal}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search books..."
            aria-label="Search books"
          />
        </div>

        <div className="selectbook-modal__panel">
          <div className="selectbook-modal__panel-head">
            <h4>All books</h4>
            <span>{books?.length || 0}</span>
          </div>

          <div id="select-book-list" className="selectbook-modal__list" role="listbox">
            {books?.length > 0 ? (
              books.map((book) => {
                const selected = selectBooks?._id === book?._id;
                return (
                  <button
                    type="button"
                    key={book?._id}
                    role="option"
                    aria-selected={selected}
                    className={`selectbook-modal__item${
                      selected ? " selectbook-modal__item--selected" : ""
                    }`}
                    onClick={() => handleSelect(book)}
                  >
                    <Radio
                      checked={selected}
                      size="small"
                      tabIndex={-1}
                      disableRipple
                      sx={{
                        padding: 0,
                        pointerEvents: "none",
                        color: "var(--grey300)",
                        "&.Mui-checked": { color: "var(--pblue)" },
                        "& .MuiSvgIcon-root": { fontSize: 18 },
                      }}
                    />
                    <span className="selectbook-modal__item-name">
                      {book?.name}
                    </span>
                  </button>
                );
              })
            ) : (
              <p className="selectbook-modal__empty">No books found</p>
            )}
          </div>
        </div>
      </Box>
    </Modal>
  );
};
